import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import SidebarFilterProvider, { SidebarFilterContext } from 'contexts/SidebarFilterContext';
import InputSidebar from 'components/Sidebar/Sidebar';
import Map from 'components/Map/Map';
import MapAiAssistant from 'components/MapChat/MapAiAssistant';
import BleachingObservationModal from 'components/Bleaching/BleachingObservationModal';
import useExpeditionTrack from 'hooks/useExpeditionTrack';
import 'components/MapChat/mapSpotlightChat.css';
import { DEFAULT_BENTHIC_CLASS_COLORS } from 'components/Tiles/BenthicTileLayer';
import { DEFAULT_REEF_EXTENT_CLASS_COLORS } from 'components/Tiles/ReefExtentTileLayer';
import { ALL_BLEACHING_YEARS } from 'utils/mapLayerSettings';
import { DEFAULT_CORTAD_MONTH, DEFAULT_CORTAD_VARIABLE, pickDefaultMonth } from 'utils/cortadLayers';

const MAP_SETTINGS_CACHE_KEY = 'customerMapSettings';

const buildDefaultBenthicClasses = () => Object.fromEntries(
  Object.entries(DEFAULT_BENTHIC_CLASS_COLORS).map(([className, color]) => [
    className,
    { visible: true, color },
  ]),
);

const buildDefaultReefExtentClasses = () => Object.fromEntries(
  Object.entries(DEFAULT_REEF_EXTENT_CLASS_COLORS).map(([className, color]) => [
    className,
    { visible: true, color },
  ]),
);

const getInitialMapSettings = () => {
  const defaults = {
    basemap: 'imagery',
    captionsVisible: true,
    benthicVisible: false,
    benthicClasses: buildDefaultBenthicClasses(),
    reefExtentVisible: false,
    reefExtentClasses: buildDefaultReefExtentClasses(),
    bleachingVisible: false,
    bleachingYear: ALL_BLEACHING_YEARS,
    cortadVisible: false,
    cortadVariable: DEFAULT_CORTAD_VARIABLE,
    cortadMonth: DEFAULT_CORTAD_MONTH,
  };

  try {
    const cached = JSON.parse(window.localStorage.getItem(MAP_SETTINGS_CACHE_KEY));
    if (!cached || typeof cached !== 'object') return defaults;
    return {
      ...defaults,
      ...cached,
      benthicClasses: {
        ...defaults.benthicClasses,
        ...(cached.benthicClasses || {}),
      },
      reefExtentClasses: {
        ...defaults.reefExtentClasses,
        ...(cached.reefExtentClasses || {}),
      },
    };
  } catch (_) {
    return defaults;
  }
};

function CustomerMapLayout({
  basemap,
  captionsVisible,
  benthicVisible,
  benthicClasses,
  reefExtentVisible,
  reefExtentClasses,
  bleachingVisible,
  bleachingYear,
  bleachingYears,
  bleachingObservations,
  setSelectedBleachingObs,
  setBasemap,
  setCaptionsVisible,
  setBenthicVisible,
  setBenthicClasses,
  setReefExtentVisible,
  setReefExtentClasses,
  setBleachingVisible,
  setBleachingYear,
  cortadVisible,
  cortadVariable,
  cortadMonth,
  cortadMonths,
  cortadMeta,
  cortadBuilding,
  setCortadVisible,
  setCortadVariable,
  setCortadMonth,
}) {
  const { filters, allProjects } = useContext(SidebarFilterContext);
  const [cortadStatus, setCortadStatus] = useState({ loading: false, zoomTooLow: false });
  const [zoneBbox, setZoneBbox] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const handleCortadStatusChange = useCallback((status) => {
    setCortadStatus((prev) => (
      prev.loading === status.loading && prev.zoomTooLow === status.zoomTooLow ? prev : status
    ));
  }, []);
  const selectedProjectName = filters?.project || '';
  const selectedProjectId = useMemo(() => {
    if (!selectedProjectName) return null;
    const project = allProjects.find((item) => item.name === selectedProjectName);
    return project?.id ?? null;
  }, [selectedProjectName, allProjects]);
  const { expeditionTrackGeoJson, hasExpeditionTrack } = useExpeditionTrack(selectedProjectId);
  const showExpeditionTrack = Boolean(selectedProjectName) && hasExpeditionTrack;

  return (
    <Container fluid className="p-0" style={{ height: 'calc(100vh - 56px)', position: 'relative', marginTop: '56px' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        <Map
          basemap={basemap}
          captionsVisible={captionsVisible}
          benthicVisible={benthicVisible}
          benthicClasses={benthicClasses}
          reefExtentVisible={reefExtentVisible}
          reefExtentClasses={reefExtentClasses}
          bleachingVisible={bleachingVisible}
          bleachingYear={bleachingYear}
          bleachingObservationsGeoJson={bleachingObservations}
          onBleachingObservationClick={setSelectedBleachingObs}
          expeditionTrackGeoJson={expeditionTrackGeoJson}
          expeditionTrackVisible={showExpeditionTrack}
          expeditionTrackFitBounds={false}
          cortadVisible={cortadVisible}
          cortadVariable={cortadVariable}
          cortadMonth={cortadMonth}
          cortadMonths={cortadMonths}
          onCortadMonthChange={setCortadMonth}
          cortadMeta={cortadMeta}
          onCortadStatusChange={handleCortadStatusChange}
          selectMode={selectMode}
          zoneBbox={zoneBbox}
          onZoneBboxChange={setZoneBbox}
          onSelectModeChange={setSelectMode}
        />
      </div>

      <MapAiAssistant
        zoneBbox={zoneBbox}
        onClearZone={() => setZoneBbox(null)}
        selectMode={selectMode}
        onToggleSelect={setSelectMode}
      />

      <div style={{
        position: 'absolute',
        top: '20px',
        right: '16px',
        zIndex: 1000,
        width: '390px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'auto',
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'flex-end',
      }}>
        <InputSidebar
          basemap={basemap}
          onBasemapChange={setBasemap}
          captionsVisible={captionsVisible}
          onCaptionsVisibleChange={setCaptionsVisible}
          benthicVisible={benthicVisible}
          onBenthicVisibleChange={setBenthicVisible}
          benthicClasses={benthicClasses}
          onBenthicClassesChange={setBenthicClasses}
          reefExtentVisible={reefExtentVisible}
          onReefExtentVisibleChange={setReefExtentVisible}
          reefExtentClasses={reefExtentClasses}
          onReefExtentClassesChange={setReefExtentClasses}
          bleachingVisible={bleachingVisible}
          onBleachingVisibleChange={setBleachingVisible}
          bleachingYear={bleachingYear}
          onBleachingYearChange={setBleachingYear}
          bleachingYears={bleachingYears}
          cortadVisible={cortadVisible}
          onCortadVisibleChange={(checked) => {
            setCortadVisible(checked);
            if (checked) setCortadStatus({ loading: true, zoomTooLow: false });
          }}
          cortadVariable={cortadVariable}
          onCortadVariableChange={(variable) => {
            setCortadVariable(variable);
            setCortadStatus((prev) => ({ ...prev, loading: !prev.zoomTooLow }));
          }}
          cortadMonth={cortadMonth}
          onCortadMonthChange={setCortadMonth}
          cortadMonths={cortadMonths}
          cortadBuilding={cortadBuilding}
          cortadLoading={cortadStatus.loading}
          cortadZoomTooLow={cortadStatus.zoomTooLow}
        />
      </div>
    </Container>
  );
}

const CustomerMap = () => {
  const initialSettings = getInitialMapSettings();
  const [basemap, setBasemap] = useState(initialSettings.basemap);
  const [captionsVisible, setCaptionsVisible] = useState(initialSettings.captionsVisible);
  const [benthicVisible, setBenthicVisible] = useState(initialSettings.benthicVisible);
  const [benthicClasses, setBenthicClasses] = useState(initialSettings.benthicClasses);
  const [reefExtentVisible, setReefExtentVisible] = useState(initialSettings.reefExtentVisible);
  const [reefExtentClasses, setReefExtentClasses] = useState(initialSettings.reefExtentClasses);
  const [bleachingVisible, setBleachingVisible] = useState(initialSettings.bleachingVisible);
  const [bleachingYear, setBleachingYear] = useState(initialSettings.bleachingYear);
  const [bleachingYears, setBleachingYears] = useState([]);
  const [bleachingObservations, setBleachingObservations] = useState(null);
  const [selectedBleachingObs, setSelectedBleachingObs] = useState(null);
  const [cortadVisible, setCortadVisible] = useState(initialSettings.cortadVisible);
  const [cortadVariable, setCortadVariable] = useState(initialSettings.cortadVariable || DEFAULT_CORTAD_VARIABLE);
  const [cortadMonth, setCortadMonth] = useState(initialSettings.cortadMonth || DEFAULT_CORTAD_MONTH);
  const [cortadMonths, setCortadMonths] = useState([]);
  const [cortadMeta, setCortadMeta] = useState(null);
  const [cortadBuilding, setCortadBuilding] = useState(false);
  const [searchParams] = useSearchParams();
  const bleachingFromUrlAppliedRef = useRef(false);

  useEffect(() => {
    if (bleachingFromUrlAppliedRef.current) return;
    const yearParam = searchParams.get('bleachingYear');
    if (!yearParam) return;
    const year = Number(yearParam);
    if (!Number.isFinite(year)) return;
    bleachingFromUrlAppliedRef.current = true;
    setBleachingYear(year);
    setBleachingVisible(true);
  }, [searchParams]);

  useEffect(() => {
    const loadBleaching = async () => {
      try {
        const [yearsRes, obsRes] = await Promise.all([
          fetch('/api/public/bleaching-years.json'),
          fetch('/api/public/bleaching-observations.geojson'),
        ]);
        if (!yearsRes.ok || !obsRes.ok) return;
        const yearsData = await yearsRes.json();
        const obsData = await obsRes.json();
        const list = yearsData.years || [];
        setBleachingYears(list);
        setBleachingObservations(obsData);
        if (list.length && bleachingYear != null && !list.includes(bleachingYear)) {
          setBleachingYear(ALL_BLEACHING_YEARS);
        }
      } catch (_) {
        // Bleaching layer optional if files missing.
      }
    };
    loadBleaching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCortad = async () => {
      try {
        const res = await fetch('/api/public/cortad/meta');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setCortadMeta(data);
        setCortadBuilding(Boolean(data.building));
        const months = data.variables?.[cortadVariable]?.months || [];
        setCortadMonths(months);
        if (months.length && !months.includes(cortadMonth)) {
          setCortadMonth(pickDefaultMonth(months));
        }
      } catch (_) {
        // Layer optional until Docker bake writes months.
      }
    };
    loadCortad();
    const timer = window.setInterval(loadCortad, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cortadVariable]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        MAP_SETTINGS_CACHE_KEY,
        JSON.stringify({
          basemap,
          captionsVisible,
          benthicVisible,
          benthicClasses,
          reefExtentVisible,
          reefExtentClasses,
          bleachingVisible,
          bleachingYear,
          cortadVisible,
          cortadVariable,
          cortadMonth,
        }),
      );
    } catch (_) {
      // Ignore localStorage quota/private mode issues.
    }
  }, [
    basemap,
    captionsVisible,
    benthicVisible,
    benthicClasses,
    reefExtentVisible,
    reefExtentClasses,
    bleachingVisible,
    bleachingYear,
    cortadVisible,
    cortadVariable,
    cortadMonth,
  ]);

  return (
    <SidebarFilterProvider>
      <CustomerMapLayout
        basemap={basemap}
        captionsVisible={captionsVisible}
        benthicVisible={benthicVisible}
        benthicClasses={benthicClasses}
        reefExtentVisible={reefExtentVisible}
        reefExtentClasses={reefExtentClasses}
        bleachingVisible={bleachingVisible}
        bleachingYear={bleachingYear}
        bleachingYears={bleachingYears}
        bleachingObservations={bleachingObservations}
        setSelectedBleachingObs={setSelectedBleachingObs}
        setBasemap={setBasemap}
        setCaptionsVisible={setCaptionsVisible}
        setBenthicVisible={setBenthicVisible}
        setBenthicClasses={setBenthicClasses}
        setReefExtentVisible={setReefExtentVisible}
        setReefExtentClasses={setReefExtentClasses}
        setBleachingVisible={setBleachingVisible}
        setBleachingYear={setBleachingYear}
        cortadVisible={cortadVisible}
        cortadVariable={cortadVariable}
        cortadMonth={cortadMonth}
        cortadMonths={cortadMonths}
        cortadMeta={cortadMeta}
        cortadBuilding={cortadBuilding}
        setCortadVisible={setCortadVisible}
        setCortadVariable={setCortadVariable}
        setCortadMonth={setCortadMonth}
      />
      <BleachingObservationModal
        show={!!selectedBleachingObs}
        onHide={() => setSelectedBleachingObs(null)}
        observation={selectedBleachingObs}
      />
    </SidebarFilterProvider>
  );
};

export default CustomerMap;
